import { classAgeYears, classesOf, forkLengthCm, type FishLife, type FishSpeciesId, type IslandFishing } from './fish';
import type { ReefSpecies } from './predation';
import { REEF_ISLANDS, type ReefIsland } from './reef';
import { COMMERCIAL_BEYOND, GOATFISH_LINE_SHARE, NONCOMMERCIAL_BEYOND } from './data/fishing/bag';
import { epochDayFromCivil } from './calendar';
import { KG_PER_POUND } from './constants';
import { COASTLINE_KM } from './turtles';

/**
 * What the player manages at a sanctuary (M165, the user's word: "both herbivore reefs by island and patrols first"):
 * the problems, not the species (`GDD.md` section 5). The rule acts on the fishing; the fish model carries it through
 * their survival and numbers (the user's chain: "Fishing regulation → effective fishing mortality → survival and
 * population dynamics → herbivory → algal and coral responses"), never a population bonus.
 *
 * **Herbivore Protection Areas**: no taking parrotfish, kala, nenue or manini on an island's reef, other fishing left as it is, as
 * Hawaiʻi did at Kahekili, Maui, in 2009 ("the take of surgeonfishes (Acanthuridae), parrotfishes ... is prohibited, but
 * there are no area-specific restrictions on fishing for other fishes", Williams et al. 2016, PLoS ONE 11: e0159100,
 * p. 3, CC0). A rule is kept imperfectly: some of the catch goes on (`PROTECTED_CATCH_SHARE`).
 *
 * **Size and bag limits** (M170, the user's word: "do size and bag limits next"; "Pick per fish group", "All of Maui
 * Nui"): for the uhu, the kala, the jacks and the octopus, a daily bag limit and a minimum size from a short list, each
 * starting at Hawaiʻi's own rules (`limits`, an index into `LIMIT_OPTIONS`, 0 Hawaiʻi's). A sanctuary's rule binds every
 * fisher on all four islands, beside the State's, the stricter of the two holding.
 */
export interface Management {
  /** Each of Maui Nui's islands' reefs, in `REEF_ISLANDS` order: whether its herbivores are protected. */
  herbivoreProtection: boolean[];
  /** Each fish group's daily bag limit and minimum size (M170): indices into `LIMIT_OPTIONS`, 0 Hawaiʻi's rules. */
  limits: Record<LimitGroup, { bag: number; size: number }>;
  /** Each island's patrols (M171), in `REEF_ISLANDS` order: an index into `PATROL_LEVELS`, 0 Hawaiʻi's coverage alone. */
  patrols: number[];
  /** Each island's bought patrol's last day, an epoch day, or none where it runs until stopped (M274: a term, `ECONOMY.patrolTerms`). */
  patrolUntil: (number | null)[];
}

/**
 * The species a Herbivore Protection Area protects: the three parrotfish and the kala (the user's word), and since M178
 * the nenue, and since M220 the manini, a surgeonfish, as Kahekili's rule protects them: "Injure, kill, possess, or remove any fish of the families Kyphosidae (sea
 * chubs, nenue), Scaridae (parrotfish, uhu), or Acanthuridae (surgeonfish, tangs)" (HAR §13-60.7-4(a), read off the
 * scanned page, M178's reader); the user's "herbivore reefs by island".
 */
export const PROTECTED_HERBIVORES: readonly FishSpeciesId[] = ['bullethead', 'palenose', 'redlip', 'kala', 'nenue', 'manini'];

/**
 * The share of a protected stock's catch that goes on (MEASURED elsewhere, derived): at Waikīkī's Marine Life
 * Conservation District, Oʻahu, 1998 to 2001, 460 shoreline patrols found fishing inside at 0.98 activities a km², the
 * permanently fished area beside it at 7.86 (Meyer 2003, PhD dissertation, University of Hawaiʻi, Table 4.4, p. 97),
 * spearfishing among them (0.38 and 0.43) catching 7.0 times what another activity does ("Spear fishing accounted for
 * 70% of total reef fish catch at Waikīkī despite accounting for only 25 % of the total number of fishing activities
 * observed", p. 91): weighted by catch, (0.60 + 7 × 0.38) / (7.43 + 7 × 0.43) = 0.31, a spear inside taken to catch
 * as one outside. One small urban reserve, its enforcement "primarily based upon public" reporting (Graham and Clark
 * 1998, p. 11 of its PDF); no measure of a Maui Nui reserve's has been read (open).
 *
 * Since M170 the share of whatever a rule forbids that is taken anyway: the fish beyond a bag limit and under a minimum
 * size too, no measure of either's keeping in Hawaiʻi having been read. A check: NOAA's intercepts since the statewide
 * uhu limit of February 2024 find 2.55% of the uhu landed beyond two a fisher a day where 15.32% were before, 0.17 of it
 * (41 fisher-days, `npm run bag`), of fishers who spoke to an interviewer.
 */
export const PROTECTED_CATCH_SHARE = 0.31;

/**
 * Patrols (M171, the user's words: "for patrols, figure it out without those papers"; "Levels per island"; "Free until
 * the budget"): officers on an island's shore and water, measured as hours a year for each km of its coast. Hawaiʻi's
 * coverage, every island's from the opening: DOCARE's statewide "NUMBER OF ENFORCEMENT HOURS", 183,750 in FY 2014, of
 * which "% OF TIME SPENT ON AQUATICS RESOURCES ENFORCEMENT" 41.45 (DLNR DOCARE, Fiscal Year 2014-2015 General Fund
 * Appropriation report to the 2015 Legislature, p. 3), over the main islands' 1,283 km of general coastline
 * (`COASTLINE_KM`, the State Data Book's): 59.4 hours a km a year, taken as Meyer's Waikīkī's (its 31% read as what
 * that coverage keeps; open, Waikīkī's own effort unread). A dedicated unit: DOCARE's Maui Community Fisheries
 * Enforcement Unit, May 2013 to June 2014, "5,089 hours to land operations, 402.5 hours to vessel patrols" on "a
 * thirteen mile stretch of coastline" (DOCARE, Report on the Evaluation of Effectiveness of Community Fisheries
 * Enforcement Units, October 2014, pp. 1, 3): 4,707 hours a year over 20.9 km, 225 a km a year above the State's.
 */
export const HAWAII_PATROL_HOURS_PER_KM = (183_750 * 0.4145) / Object.values(COASTLINE_KM).reduce((a, b) => a + b, 0);
export const UNIT_PATROL_HOURS_PER_KM = (((5_089 + 402.5) * 12) / 14) / (13 * 1.609344);

/**
 * The share of what a rule forbids taken anyway at a patrol effort (hours a km of coast a year): falling as the chance of
 * being caught rises, 1 − e^(−µE) as Millage et al. 2021 take detection (an exponential, the officers' hours spread
 * over the coast), anchored at Hawaiʻi's coverage at Meyer's 0.31 and at the Maui unit's at 0.12, "The average
 * compliance rate for the year was 88%", the mean of its months' rates over 468 fisher inspections (the DOCARE
 * report, pp. 6-7; MEASURED there, among the fishers its officers chose to inspect, every rule at once): µ = ln(0.31 / 0.12) / 225 = 0.0042 a (hour a km).
 * Compliance among the inspected and the catch a rule forbids taken anyway are not the same measure (open).
 */
const UNIT_TAKEN_ANYWAY = 0.12;
const PATROL_DECAY = Math.log(PROTECTED_CATCH_SHARE / UNIT_TAKEN_ANYWAY) / UNIT_PATROL_HOURS_PER_KM;
export function takenAnywayAt(hoursPerKm: number): number {
  return PROTECTED_CATCH_SHARE * Math.exp(-PATROL_DECAY * (hoursPerKm - HAWAII_PATROL_HOURS_PER_KM));
}

/** The Maui unit's stretch of coast, km: "a thirteen mile stretch of coastline" (the DOCARE report above, p. 1). */
const UNIT_STRETCH_KM = 13 * 1.609344;

/**
 * The patrol levels an island may have: Hawaiʻi's officers alone; one stretch of its coast patrolled as the Maui unit
 * patrolled its 13 miles (M274, the redesign's section 5: a focused patrol a small sanctuary can pay for, the unit's
 * own reach); its whole coast patrolled as closely, or twice as closely (M171's reviewer: "a dedicated unit" read as
 * one unit, where Maui's 193 km at its density are about nine units' hours). A level's `stretchKm` is the coast it
 * covers, the whole coast where it has none.
 */
export const PATROL_LEVELS: readonly { name: string; hoursPerKm: number; stretchKm?: number }[] = [
  { name: 'by Hawaiʻi\'s officers alone', hoursPerKm: HAWAII_PATROL_HOURS_PER_KM },
  { name: 'along one stretch as the Maui unit patrolled its 13 miles', hoursPerKm: HAWAII_PATROL_HOURS_PER_KM + UNIT_PATROL_HOURS_PER_KM, stretchKm: UNIT_STRETCH_KM },
  { name: 'as closely as the Maui unit', hoursPerKm: HAWAII_PATROL_HOURS_PER_KM + UNIT_PATROL_HOURS_PER_KM },
  { name: 'twice as closely as the Maui unit', hoursPerKm: HAWAII_PATROL_HOURS_PER_KM + 2 * UNIT_PATROL_HOURS_PER_KM },
];

/** The coast a level patrols on an island, km: its stretch, no longer than the island's coast, or the whole coast. */
const patrolCoastKm = (island: ReefIsland, level: number): number => Math.min(PATROL_LEVELS[level]?.stretchKm ?? Infinity, COASTLINE_KM[island]);

/** The officers' hours a year a level adds to an island's patrols, beside Hawaiʻi's: its hours a km over the coast it patrols. */
export function patrolHoursAdded(island: ReefIsland, level: number): number {
  return ((PATROL_LEVELS[level]?.hoursPerKm ?? HAWAII_PATROL_HOURS_PER_KM) - HAWAII_PATROL_HOURS_PER_KM) * patrolCoastKm(island, level);
}

/**
 * An island's share of what a rule forbids taken anyway at a patrol level: on the coast it patrols as its hours keep
 * it, on the rest as Hawaiʻi's officers alone keep it, each by its share of the coast (JUDGED: the fishing spread
 * along the coast by its length; NOAA's interviews give no fishing by stretch, M274).
 */
export function takenAnywayOn(island: ReefIsland, level: number): number {
  const at = PATROL_LEVELS[level] ?? PATROL_LEVELS[0];
  const share = patrolCoastKm(island, level) / COASTLINE_KM[island];
  const patrolled = takenAnywayAt(at?.hoursPerKm ?? HAWAII_PATROL_HOURS_PER_KM);
  return share >= 1 ? patrolled : share * patrolled + (1 - share) * takenAnywayAt(HAWAII_PATROL_HOURS_PER_KM);
}

/** An island's share of what a rule forbids taken anyway, by its patrol level. */
function keptOn(management: Management, island: number): number {
  return takenAnywayOn(REEF_ISLANDS[island] as ReefIsland, management.patrols[island] ?? 0);
}

/** The fish groups a sanctuary's limits are set for, as Hawaiʻi's rules group them (HAR 13-95-1's "uhu", "kala", "ulua" and "heʻe"; Maui's "moano kea", HAR 13-95.1-20, M179). */
export const LIMIT_GROUPS = ['uhu', 'kala', 'jacks', 'octopus', 'goatfish'] as const;
export type LimitGroup = (typeof LIMIT_GROUPS)[number];

/** Each fished species' group (the roi has no rule, HAR 13-95 naming none). */
const LIMIT_GROUP_OF: Partial<Record<FishSpeciesId, LimitGroup>> = {
  bullethead: 'uhu', palenose: 'uhu', redlip: 'uhu', kala: 'kala', ulua: 'jacks', omilu: 'jacks', octopus: 'octopus', moanokea: 'goatfish',
};

/** The uhu Hawaiʻi's rules hold to the larger minimum since 2014 on Maui and 2024 statewide: the redlip (uhu pālukaluka) among the game's (HAR 13-95-16(a)). */
const LARGE_UHU: readonly FishSpeciesId[] = ['redlip'];

/**
 * A group's rule as it binds the fish: the noncommercial fishers' bag a day (null none), the commercial fishers' (null
 * none; 'closed', none may be taken for sale), the minimum length (inches, the rules' "length", tip of the snout
 * to the middle of the tail's trailing edge, a fork length) or the octopus's weight (pounds), the larger uhu's minimum,
 * and whether the terminal-phase redlip (uhu ʻeleʻele) is protected.
 */
export interface Rule {
  bag: number | null;
  commercialBag: number | null | 'closed';
  minimum: number;
  largeMinimum?: number;
  terminalProtected?: boolean;
}

/**
 * The rules a stock's measured fishing was assessed under (its `fishingPerYear`, `fish.ts`): the parrotfish's and the
 * jacks' NOAA's of 2005 to 2016 (Nadon 2017), under HAR 13-95 as amended 12/19/02 (uhu 12 inches, no bag, §13-95-16;
 * ulua 10 inches, 20 a day noncommercial, §13-95-22; Maui's island rule of November 2014 the last two years only, open);
 * the kala's Maui's own fishery of May 2024 to June 2025 (Angst et al. 2026), under the statewide rule of 22 February
 * 2024 (14 inches, 4 a day noncommercial, the commercial fishery under its annual limit, "near the current status quo",
 * BLNR F-5 of 7 December 2023, p. 7); the octopus's its pound since 2002 (§13-95-55); the moano kea's Nadon's catch of
 * 2005 to 2016 under no rule but Maui's of November 2014, its last two years (open, as the parrotfish's).
 */
const ASSESSED_UNDER: Record<LimitGroup, Rule & { commercialBag: number | null }> = {
  uhu: { bag: null, commercialBag: null, minimum: 12 },
  kala: { bag: 4, commercialBag: null, minimum: 14 },
  jacks: { bag: 20, commercialBag: null, minimum: 10 },
  octopus: { bag: null, commercialBag: null, minimum: 1 },
  goatfish: { bag: null, commercialBag: null, minimum: 0 },
};

/** The days Hawaiʻi's rules changed: Maui's island rule took effect (HAR 13-95.1, "[Eff 11/1/14]"), and the statewide rule ("took effect on February 22, 2024", DAR's Holomua page; HAR 13-95 "am and comp 2/22/24"). */
export const MAUI_RULE_DAY = epochDayFromCivil(2014, 11, 1);
export const STATE_RULE_DAY = epochDayFromCivil(2024, 2, 22);

/**
 * Hawaiʻi's rules on a day. Before Maui's island rule, HAR 13-95 as amended 12/19/02: uhu 12 inches, no bag
 * (§13-95-16); kala 14 inches, no bag (§13-95-11); ulua 10 inches, 20 a day noncommercial (§13-95-22); heʻe a pound
 * (§13-95-55). From 1 November 2014 on Maui island, its rule for the uhu (below). From 22 February 2024 the statewide
 * rules (HAR 13-95 as amended and compiled 2/22/24, DAR's unofficial copy), and Maui's island rule beside them
 * (HAR 13-95.1-21, "waters of Maui island", Kahoʻolawe's excluded):
 * - uhu: 10 inches, the large species 14 (§13-95-16(a), (b)); 2 a day for every fisher but a commercial one (§13-95-16(c);
 *   "a new noncommercial bag limit of two uhu per person per day", BLNR F-5 of 7 December 2023, p. 1); none but the
 *   redlip, both its phases, taken for sale ("Prohibiting the commercial harvest of any uhu species other than Scarus
 *   rubroviolaceus", p. 1; "will prevent commercial take of small-bodied uhu species", p. 8; §13-95-16(d), (f)(1), (h)(1):
 *   a "commercial uhu fishing trip" is one "where any uhu are taken for a commercial purpose", §13-95-1; M170's third
 *   reviewer, the second's reading of it reversed), the redlip's commercial fishers under no bag and a 30,000 lb annual
 *   limit (not modeled: its share of Maui Nui unread, open); on Maui island, from 1 November 2014, everyone 2 a day ("No
 *   person may take more than two uhu of any variety per day", §13-95.1-21(d); the chapter's take provisions "supersede
 *   any conflicting" ones, §13-95.1-3(c), the statewide ban on selling the others since 2024 conflicting with none of
 *   them), 10 inches ("Any other department size restriction notwithstanding ... any person may take any other uhu
 *   greater than ten inches in length", §13-95.1-21(c)) and the large species 14 (§13-95.1-21(b)), and no terminal-phase
 *   redlip ("No person may take or possess any uhu ʻeleʻele or uhu uliuli at any time", §13-95.1-21(a));
 * - kala: 14 inches, 4 a day, a commercial permittee exempt under a 15,000 lb annual limit (§13-95-11);
 * - ulua (both jacks): 10 inches, 20 a day, a commercial marine licensee exempt (§13-95-22);
 * - heʻe: a pound (§13-95-55);
 * - moano kea (M179): on Maui island from 1 November 2014, 12 inches ("No person may take or possess any kūmū, moano
 *   kea, or weke nono less than twelve inches in length", §13-95.1-20(a)) and 2 a day for every fisher ("No person may
 *   take more than two moano kea per day", §13-95.1-20(d)); elsewhere no rule (DAR's summary: "Moano kea Open", "12
 *   inches (Maui only)", "2 (Maui only)").
 */
export function hawaiiRule(group: LimitGroup, island: ReefIsland, species: FishSpeciesId, epochDay: number): Rule {
  const statewide = epochDay >= STATE_RULE_DAY;
  if (group === 'kala') return statewide ? ASSESSED_UNDER.kala : { bag: null, commercialBag: null, minimum: 14 };
  const maui = island === 'maui' && epochDay >= MAUI_RULE_DAY;
  if (group === 'goatfish') return maui ? { bag: 2, commercialBag: 2, minimum: 12 } : ASSESSED_UNDER.goatfish;
  if (group !== 'uhu') return ASSESSED_UNDER[group];
  if (!maui && !statewide) return ASSESSED_UNDER.uhu;
  return {
    bag: 2,
    commercialBag: !statewide ? 2 : !LARGE_UHU.includes(species) ? 'closed' : maui ? 2 : null,
    minimum: 10,
    largeMinimum: 14,
    terminalProtected: maui,
  };
}

/**
 * The limits a sanctuary may set, each group's first Hawaiʻi's own (no rule of the sanctuary's): a bag a day for every
 * fisher (0, none may be taken) and a minimum size, from Hawaiʻi's rules and its proposals. The uhu: 1 a day, or none;
 * the redlip at 15 inches (about Nadon 2017's minimum size for a 10% chance of overfishing it, 386 mm, 15.2 inches, p.
 * 192; 15 inches, 381 mm, about 13%), the small uhu at Hawaiʻi's 10 (their old 12 is the rule they were assessed under,
 * and a size those rules forbade is taken as the assessment's selectivity shows it taken: a 12 would change nothing,
 * M170's reviewer). The kala: 2 a day (Kīpahulu's, HAR 13-60.11-4(b)(4)), 1, or none; 16 or 18 inches (DAR's
 * Sustainable Herbivore Management Plan, p. 49: "Minimum Size limit: 18 inches (OFL)"). The jacks: 5 a day (Maui's 2013
 * proposal, "Take more than five ulua of any variety per day", withdrawn), 1 (Kīpahulu's ʻōmilu, §13-60.11-4(b)(3)), or
 * none; 16 inches (Hawaiʻi's least for sale, §13-95-22(b)). The octopus: 2 pounds (a choice); no bag (no record of its
 * catch a day read: NOAA's intercepts count fish alone). The moano kea (M179; a game choice, M179: a limit as the other
 * groups', the player's to set): 1 a day (Maui's kūmū's, "No person may take more than one kūmū per day",
 * §13-95.1-20(c)), or none; Maui's 12 inches on every island.
 */
export const LIMIT_OPTIONS: Record<LimitGroup, { bags: readonly (number | null)[]; sizes: readonly ({ minimum: number; largeMinimum?: number } | null)[] }> = {
  uhu: { bags: [null, 1, 0], sizes: [null, { minimum: 0, largeMinimum: 15 }] },
  kala: { bags: [null, 2, 1, 0], sizes: [null, { minimum: 16 }, { minimum: 18 }] },
  jacks: { bags: [null, 5, 1, 0], sizes: [null, { minimum: 16 }] },
  octopus: { bags: [null], sizes: [null, { minimum: 2 }] },
  goatfish: { bags: [null, 1, 0], sizes: [null, { minimum: 12 }] },
};

/** Each group's name for a player. */
export const LIMIT_NAMES: Record<LimitGroup, string> = { uhu: 'parrotfish (uhu)', kala: 'kala', jacks: 'ulua and ʻōmilu', octopus: 'heʻe (day octopus)', goatfish: 'moano kea (blue goatfish)' };

/** An option's words for a player: Hawaiʻi's, a bag a fisher a day or none, a minimum length or weight. */
export function limitWords(group: LimitGroup, limit: 'bag' | 'size', option: number): string {
  if (option === 0) return 'Hawaiʻi\'s rule';
  if (limit === 'bag') {
    const n = LIMIT_OPTIONS[group].bags[option];
    return n === 0 ? 'none may be taken' : `${String(n)} a fisher a day`;
  }
  const s = LIMIT_OPTIONS[group].sizes[option];
  if (!s) return 'Hawaiʻi\'s rule';
  if (group === 'octopus') return `${s.minimum} pounds at least`;
  return s.largeMinimum !== undefined ? `the redlip ${s.largeMinimum} inches at least` : `${s.minimum} inches at least`;
}

/** No limits of the sanctuary's: Hawaiʻi's alone. */
export const hawaiiLimits = (): Management['limits'] => ({ uhu: { bag: 0, size: 0 }, kala: { bag: 0, size: 0 }, jacks: { bag: 0, size: 0 }, octopus: { bag: 0, size: 0 }, goatfish: { bag: 0, size: 0 } });

/** A sanctuary's management as it opens: nothing protected and Hawaiʻi's rules, where it has reef fish to protect; none elsewhere. */
export function openingManagement(hasFish: boolean): Management | null {
  return hasFish ? { herbivoreProtection: REEF_ISLANDS.map(() => false), limits: hawaiiLimits(), patrols: REEF_ISLANDS.map(() => 0), patrolUntil: REEF_ISLANDS.map(() => null) } : null;
}

const minBag = (a: number | null, b: number | null): number | null => (a === null ? b : b === null ? a : Math.min(a, b));

/** The rule on an island on a day: Hawaiʻi's, and the sanctuary's where it set one, the stricter holding. */
export function ruleOn(group: LimitGroup, island: ReefIsland, species: FishSpeciesId, limits: Management['limits'], epochDay: number): Rule {
  const state = hawaiiRule(group, island, species, epochDay);
  const options = LIMIT_OPTIONS[group];
  const bag = options.bags[limits[group].bag] ?? null;
  const size = options.sizes[limits[group].size] ?? null;
  return {
    bag: minBag(state.bag, bag),
    commercialBag: state.commercialBag === 'closed' ? 'closed' : minBag(state.commercialBag, bag),
    minimum: Math.max(state.minimum, size?.minimum ?? 0),
    ...(state.largeMinimum !== undefined || size?.largeMinimum !== undefined ? { largeMinimum: Math.max(state.largeMinimum ?? 0, size?.largeMinimum ?? 0) } : {}),
    ...(state.terminalProtected ? { terminalProtected: true } : {}),
  };
}

/**
 * Each stock's commercial share of its catch (DERIVED): the parrotfish's, 40,450 lb a year of the State's commercial
 * reports against 82,075 of NOAA's recreational estimate (Williams and Ma 2013, PIFSC Administrative Report H-13-04,
 * Table 3, p. 21, 2004 to 2009 and 2004 to 2011, all parrotfish); the kala's, 11,282 kg of 20,197 (Nadon 2017, p. 69,
 * its commercial three kala species); the ulua's 6,689 kg of 142,429 and the ʻōmilu's 3,147 of 148,127 (pp. 81, 88);
 * the moano kea's 700 of 4,474 (p. 136).
 */
const COMMERCIAL_SHARE: Partial<Record<FishSpeciesId, number>> = {
  bullethead: 40_450 / (40_450 + 82_075), palenose: 40_450 / (40_450 + 82_075), redlip: 40_450 / (40_450 + 82_075),
  kala: 11_282 / 20_197, ulua: 6_689 / 142_429, omilu: 3_147 / 148_127, octopus: 0, moanokea: 700 / 4_474,
};

/**
 * The share of a group's fish caught on a line, whose undersized are caught and let go (a spear's or a net's are not
 * taken where the rule is kept): the parrotfish and the kala, Waikīkī's, "acanthurids and scarids collectively accounted
 * for 52% of the total spear catch of reef fishes, but only 2% of pole & line catches", the spears 70% of the catch
 * (Meyer 2003, pp. 88, 91): 0.02 × 0.30 / (0.52 × 0.70 + 0.02 × 0.30) = 0.016; the jacks taken on a line (DAR's
 * tagging of them by hook and line, Tagawa and Tam 2006; a choice, open); the octopus by hand and spear; the moano kea
 * NOAA's intercepts' share of its fish landed on a hook and line (M179, `GOATFISH_LINE_SHARE`, MEASURED).
 */
const LINE_SHARE: Record<LimitGroup, number> = { uhu: (0.02 * 0.3) / (0.52 * 0.7 + 0.02 * 0.3), kala: (0.02 * 0.3) / (0.52 * 0.7 + 0.02 * 0.3), jacks: 1, octopus: 0, goatfish: GOATFISH_LINE_SHARE };

/**
 * The share of a fish caught on a line and let go that dies: 0.074, the recreational fishery's at the surface in
 * Campbell et al. 2014's meta-analysis of the red snapper (Fish. Bull. 112: 283-296, Table 4, p. 294; 0.089 at 10 m):
 * another sea's and another fish's, none of Hawaiʻi's read (open).
 */
const RELEASE_DEATHS = 0.074;

/** What a sector's bag of N a day leaves of the catch it would take under no bag, kept as rules are kept there (its share taken anyway): 1 − (1 − taken anyway) × the share beyond. */
function leftBy(beyondN: readonly number[], bag: number | null, anyway: number): number {
  const removed = bag === null ? 0 : (beyondN[Math.min(bag, beyondN.length - 1)] as number);
  return 1 - (1 - anyway) * removed;
}

const CM_PER_INCH = 2.54;

/** The share of a forbidden fish taken anyway where rules are kept so (`takenAnywayAt`), and of the rest a line's let go that die (a spear's or a net's are not taken where the rule is kept). */
const forbiddenTaken = (group: LimitGroup, anyway: number): number => anyway + (1 - anyway) * LINE_SHARE[group] * RELEASE_DEATHS;

/** The share of a class a rule's sizes and phase allow: none under its minimum (the octopus's by its weight), its terminal phase none where that is protected. */
function allowedShare(life: FishLife, group: LimitGroup, rule: Rule, step: number, cls: number): number {
  const k = classesOf(life)[step]?.[cls];
  if (!k) return 1;
  const large = LARGE_UHU.includes(life.id);
  const legal = group === 'octopus' ? k.weightKg >= rule.minimum * KG_PER_POUND
    : forkLengthCm(life, classAgeYears(life, cls, step)) >= (large && rule.largeMinimum !== undefined ? rule.largeMinimum : rule.minimum) * CM_PER_INCH;
  if (!legal) return 0;
  return rule.terminalProtected && large ? 1 - k.terminal : 1;
}

/** A stock's catch by sector: its share of the catch it was assessed at, its share beyond N a day, whether it is shut now (closed, or a bag of none), and its bag now and then. */
interface Sector {
  share: number;
  beyond: readonly number[];
  shut: boolean;
  now: number | null;
  assessed: number | null;
}

function sectorsOf(species: FishSpeciesId, group: LimitGroup, rule: Rule): Sector[] {
  if (group === 'octopus') return [{ share: 1, beyond: [1], shut: false, now: null, assessed: null }];
  const commercial = COMMERCIAL_SHARE[species] ?? 0;
  const assessed = ASSESSED_UNDER[group];
  const c = rule.commercialBag;
  return [
    { share: commercial, beyond: group === 'jacks' || group === 'goatfish' ? NONCOMMERCIAL_BEYOND[group] : COMMERCIAL_BEYOND[group], shut: c === 'closed' || c === 0, now: c === 'closed' ? 0 : c, assessed: assessed.commercialBag },
    { share: 1 - commercial, beyond: NONCOMMERCIAL_BEYOND[group], shut: rule.bag === 0, now: rule.bag, assessed: assessed.bag },
  ];
}

/**
 * A class's take under a rule against its take under the rules its stock was assessed under (its own selectivity's,
 * however those rules were kept: Nadon 2017, "it is uncertain to what degree these are respected by fishermen"), its
 * sectors weighed by their shares of that catch (M170's reviewer: a fish forbidden twice was counted twice). A ban (a
 * Herbivore Protection Area, a sector closed, a bag of none) leaves every class what is taken anyway of the take no rule would leave; so
 * does a size or phase the assessed rules allowed and this one forbids, the bag not reaching it (a fish forbidden twice
 * counted once). Every other class, the sizes this rule allows and those the assessed rules forbade too (taken as the
 * selectivity shows them taken), keeps its bag's share of its take against the assessed rules' bag: a bag cuts the
 * day's fish of every size, as its curve was counted on every fish landed (M170's second reviewer: the sizes under a
 * minimum were left uncut, and a size newly allowed taken less than before). Patrols keep the old rules too (M171's
 * reviewer: they reached none of them): a size both forbid, taken as Hawaiʻi's coverage kept it, is taken in the
 * share the island's patrols leave of that coverage's, a line's released fish dying in it as any forbidden fish's
 * (M171's reviewer: the jacks' undersized lost their release deaths).
 */
function classFactor(life: FishLife, group: LimitGroup, rule: Rule, banned: boolean, kept: number, sectors: readonly Sector[], step: number, cls: number): number {
  const before = allowedShare(life, group, ASSESSED_UNDER[group], step, cls);
  const now = allowedShare(life, group, rule, step, cls);
  const newly = Math.max(0, before - now);
  const both = Math.max(0, Math.min(1 - before, 1 - now));
  const anyway = forbiddenTaken(group, kept);
  let f = 0;
  for (const s of sectors) {
    const left = leftBy(s.beyond, s.assessed, PROTECTED_CATCH_SHARE);
    const bag = leftBy(s.beyond, s.now, kept) / left;
    f += s.share * (banned || s.shut ? anyway / left : now * bag + newly * (anyway / left) + both * bag * (anyway / forbiddenTaken(group, PROTECTED_CATCH_SHARE)));
  }
  return f;
}

/**
 * A stock's fishing under a rule: its rate on the sizes it takes in full (the classes no rule newly forbids) over the
 * assessed rate, and each step's classes' shares taken against that (`classesOf`'s times each class's take over the
 * full classes'), none where every class is as the full ones (every class taken as it was assessed, but the rate);
 * a rule kept where its share is taken anyway (`kept`, the island's patrols'), its assessed rules as they were kept,
 * at Hawaiʻi's coverage (M171). Made once for each life, rule and keeping.
 */
const fishingBy = new Map<FishLife, Map<string, { full: number; taken: readonly (readonly number[])[] | undefined }>>();
function fishingUnder(life: FishLife, group: LimitGroup, rule: Rule, banned: boolean, kept = PROTECTED_CATCH_SHARE): { full: number; taken: readonly (readonly number[])[] | undefined } {
  let byRule = fishingBy.get(life);
  if (!byRule) fishingBy.set(life, (byRule = new Map()));
  const key = `${rule.bag}|${rule.commercialBag}|${rule.minimum}|${rule.largeMinimum ?? ''}|${rule.terminalProtected ? 1 : 0}|${banned ? 1 : 0}|${kept}`;
  const made = byRule.get(key);
  if (made) return made;
  const sectors = sectorsOf(life.id, group, rule);
  const anyway = forbiddenTaken(group, kept);
  const full = sectors.reduce((a, s) => {
    const left = leftBy(s.beyond, s.assessed, PROTECTED_CATCH_SHARE);
    return a + s.share * (banned || s.shut ? anyway / left : leftBy(s.beyond, s.now, kept) / left);
  }, 0);
  const factors = classesOf(life).map((step, s) => step.map((_, c) => classFactor(life, group, rule, banned, kept, sectors, s, c)));
  const same = factors.every((step) => step.every((x) => x === full));
  const result = { full, taken: same ? undefined : classesOf(life).map((step, s) => step.map((k, c) => (k.taken * (factors[s]?.[c] as number)) / full)) };
  byRule.set(key, result);
  return result;
}

/**
 * Each island's fishing on a species under the sanctuary's management on a day (M165, M170): the stock's measured rate
 * times its rule's rate on the sizes taken in full there over its assessed rules' (`fishingUnder`), and the shares
 * taken under its rule there, kept as its patrols keep it (M171); a Herbivore Protection Area a ban on a protected
 * island's herbivores; none where the
 * sanctuary has no management, every island the stock's. Effort is taken as it was (none moved to the fish left, as
 * NOAA's own assessments take it: "Effort is also assumed not to change in response to the establishment of a bag
 * limit", Ahrens and Nadon 2023, p. 13; open).
 */
export function islandFishing(sp: ReefSpecies, management: Management | null, epochDay: number): readonly IslandFishing[] | undefined {
  if (!management) return undefined;
  const id = sp.life.id;
  const group = LIMIT_GROUP_OF[id];
  const f = sp.supply.fishingPerYear;
  const protectable = PROTECTED_HERBIVORES.includes(id);
  // A herbivore no rule limits (the nenue, M178: HAR 13-95 names no chub): its fishing as it was, but on a protected
  // island the share of it taken anyway, the ban's whole catch (`keptOn`, the patrols'). The chubs a line takes and lets
  // go that die are not added, as the parrotfish's are (`forbiddenTaken`): no measure of the chubs' catch by gear was
  // found (open, M178's reviewer).
  if (!group) {
    if (!protectable || !management.herbivoreProtection.some((p) => p)) return undefined;
    return REEF_ISLANDS.map((_, i) => ({ perYear: f * (management.herbivoreProtection[i] === true ? keptOn(management, i) : 1) }));
  }
  return REEF_ISLANDS.map((island, i) => {
    const rule = ruleOn(group, island, id, management.limits, epochDay);
    const under = fishingUnder(sp.life, group, rule, protectable && management.herbivoreProtection[i] === true, keptOn(management, i));
    const perYear = f * under.full;
    return under.taken ? { perYear, taken: under.taken } : { perYear };
  });
}

/** An island's place in `REEF_ISLANDS`, or none for a name the reef does not have. */
export function islandIndex(island: string): number | null {
  const i = REEF_ISLANDS.indexOf(island as ReefIsland);
  return i < 0 ? null : i;
}
